import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Product } from "@/entities/Product";
import { Star, SlidersHorizontal, X } from "lucide-react";

const CATEGORIES = ["All", "Artist (Entertainment)", "Content Creator", "Craftsman (Build To Spec)", "Healer (Remedies; Protocol)", "Manifestor", "Motivator", "Soul Worker (Passion Service)", "Problem Solver", "Visionary"];
const SORT_OPTIONS = [
  { label: "Newest", value: "-createdAt" },
  { label: "Price: Low to High", value: "price" },
  { label: "Price: High to Low", value: "-price" },
  { label: "Creator Services", value: "-rating" },
];

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`w-3 h-3 ${s <= Math.round(rating) ? "fill-[#c8a96e] text-[#c8a96e]" : "text-gray-300"}`} />
      ))}
    </div>
  );
}

function ProductCard({ product }) {
  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  return (
    <Link
      to={createPageUrl(`ProductDetail?id=${product.id}`)}
      className="group bg-white rounded-xl overflow-hidden border border-[#e8e4de] hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative overflow-hidden aspect-square bg-[#f5f3ef]">
        <img
          src={product.images?.[0]}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.is_new && (
            <span className="bg-[#1a1a1a] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">New</span>
          )}
          {discount && (
            <span className="bg-[#c8a96e] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">-{discount}%</span>
          )}
        </div>
        {product.status === "sold_out" && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="bg-[#1a1a1a] text-white text-xs font-semibold px-3 py-1 rounded-full">Sold Out</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-[#c8a96e] font-medium mb-1">{product.creator_name}</p>
        <h3 className="font-medium text-[#1a1a1a] text-sm leading-snug mb-2 group-hover:text-[#c8a96e] transition-colors line-clamp-2">
          {product.name}
        </h3>
        <div className="flex items-center gap-1.5 mb-3">
          <StarRating rating={product.rating || 0} />
          <span className="text-xs text-gray-400">({product.review_count})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-[#1a1a1a]">${product.price}</span>
          {product.original_price && (
            <span className="text-xs text-gray-400 line-through">${product.original_price}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState("-createdAt");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get("category");
    if (cat && CATEGORIES.includes(cat)) setActiveCategory(cat);
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const filters = { status: "active" };
      if (activeCategory !== "All") filters.category = activeCategory;
      const data = await Product.filter(filters, sortBy, 50);
      setProducts(data);
      setLoading(false);
    }
    load();
  }, [activeCategory, sortBy]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-1">Creators Warehouse</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#1a1a1a]">Shop All Products</h1>
        <p className="text-gray-500 mt-2 text-sm">Handmade, handpicked, and made with love by our community of creators.</p>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        {/* Category Tabs - scrollable */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                activeCategory === cat
                  ? "bg-[#1a1a1a] text-white"
                  : "bg-white border border-[#e8e4de] text-[#1a1a1a] hover:border-[#c8a96e] hover:text-[#c8a96e]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <SlidersHorizontal className="w-4 h-4 text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-[#e8e4de] rounded-lg px-3 py-1.5 bg-white text-[#1a1a1a] focus:outline-none focus:border-[#c8a96e]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Active filter badge */}
      {activeCategory !== "All" && (
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-gray-500">Filtering by:</span>
          <span className="inline-flex items-center gap-1.5 bg-[#c8a96e]/10 text-[#c8a96e] text-xs font-semibold px-3 py-1 rounded-full">
            {activeCategory}
            <button onClick={() => setActiveCategory("All")}>
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {/* Product Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#e8e4de] overflow-hidden animate-pulse">
              <div className="aspect-square bg-gray-100" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-1/3" />
                <div className="h-4 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-5xl mb-4">🎨</div>
          <h3 className="font-display text-xl font-bold text-[#1a1a1a] mb-2">No products found</h3>
          <p className="text-gray-500 text-sm">Try a different category or check back soon.</p>
          <button
            onClick={() => setActiveCategory("All")}
            className="mt-4 text-sm text-[#c8a96e] font-medium hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-400 mb-4">{products.length} product{products.length !== 1 ? "s" : ""} found</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </>
      )}
    </div>
  );
}